import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { Box, Button, ButtonGroup, IconButton, MenuItem, TextField } from "@mui/material";
import { FormEvent, useMemo, useRef, useState } from "react";
import { IconColorPicker } from "../../components/IconColorPicker";
import { SidePanel } from "../../components/SidePanel";
import { CategoryIcon } from "../../lib/format";
import type { GroupAccountDraft, TransactionType } from "../../lib/types";
import { newDraftId, type SelectableCategoryDraft } from "./groupSetupDraft";

export const accountTypeDefaults: Record<string, { icon: string; color: string; label: string }> = {
  bank: { icon: "boc", color: "#2563eb", label: "Bank" },
  card: { icon: "wallet", color: "#7c3aed", label: "Card" },
  cash: { icon: "cash", color: "#2563eb", label: "Cash" },
  savings: { icon: "safe", color: "#b7791f", label: "Savings" },
  debt: { icon: "coins", color: "#b42318", label: "Debt" },
};

function PanelHeader({ title, titleId, onClose }: { title: string; titleId: string; onClose: () => void }) {
  return (
    <Box className="records-modal-toolbar">
      <h2 id={titleId}>{title}</h2>
      <IconButton className="records-icon-button" type="button" aria-label="Close setup panel" title="Close" onClick={onClose}>
        <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M18 6 6 18"></path>
          <path d="m6 6 12 12"></path>
        </svg>
      </IconButton>
    </Box>
  );
}

export function CategoryDraftPanel({
  categories,
  category,
  initialType = "expense",
  onClose,
  onDelete,
  onSave,
}: {
  categories: SelectableCategoryDraft[];
  category?: SelectableCategoryDraft;
  initialType?: TransactionType;
  onClose: () => void;
  onDelete: (categoryId: string) => void;
  onSave: (category: SelectableCategoryDraft) => void;
}) {
  const [type, setType] = useState<TransactionType>(category?.type || initialType);
  const [parentId, setParentId] = useState(category?.parent_client_id || "");
  const [icon, setIcon] = useState(category?.icon || "tag");
  const [color, setColor] = useState(category?.color || "#2563eb");
  const [name, setName] = useState(category?.name || "");
  const [error, setError] = useState("");
  const closeRef = useRef(onClose);
  const hasChildren = Boolean(category && categories.some((item) => item.parent_client_id === category.client_id));
  const parents = useMemo(
    () => categories.filter((item) => !item.parent_client_id && item.type === type && item.client_id !== category?.client_id),
    [categories, category?.client_id, type],
  );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) return setError("Name is required.");
    if (categories.some((item) => item.client_id !== category?.client_id && item.type === type && item.name.trim().toLowerCase() === trimmedName.toLowerCase())) {
      return setError("A category with this name already exists for this type.");
    }
    onSave({
      client_id: category?.client_id || newDraftId("category"),
      type,
      name: trimmedName,
      icon,
      color,
      parent_client_id: hasChildren ? null : parentId || null,
      selected: category?.selected ?? true,
      is_custom: category?.is_custom ?? true,
    });
    closeRef.current();
  };

  return (
    <SidePanel ariaLabelledBy="setupCategoryPanelTitle" className="category-modal group-setup-side-panel" onClose={onClose}>
      {({ close }) => {
        closeRef.current = close;
        return (
          <>
            <PanelHeader title={category ? "Edit category" : "Add category"} titleId="setupCategoryPanelTitle" onClose={close} />
            <form className="records-form category-form" onSubmit={submit}>
              <Box className="category-type-toggle">
                <ButtonGroup variant="outlined" aria-label="Category type">
                  {(["expense", "income"] as TransactionType[]).map((option) => (
                    <Button key={option} type="button" disabled={hasChildren} variant={type === option ? "contained" : "outlined"} onClick={() => { setType(option); setParentId(""); }}>
                      {option === "expense" ? "Expense" : "Income"}
                    </Button>
                  ))}
                </ButtonGroup>
              </Box>
              <TextField select label="Parent category" value={parentId} disabled={hasChildren} onChange={(event) => setParentId(String(event.target.value))}>
                <MenuItem value="">No parent</MenuItem>
                {parents.map((parent) => <MenuItem key={parent.client_id} value={parent.client_id}>{parent.name}</MenuItem>)}
              </TextField>
              <Box className="category-controls-row">
                <IconColorPicker color={color} icon={icon} pickerId="setupCategoryIconPicker" pickerLabel="Category icon" onColorChange={setColor} onIconChange={setIcon} />
                <TextField fullWidth required label="Name" value={name} onChange={(event) => { setName(event.target.value); setError(""); }} error={Boolean(error)} helperText={error} />
              </Box>
              <Box className="records-modal-actions">
                {category ? <Button variant="outlined" color="error" type="button" startIcon={<DeleteOutlineIcon />} onClick={() => { onDelete(category.client_id); close(); }}>Delete category</Button> : null}
                <Button variant="contained" type="submit">{category ? "Save changes" : "Add category"}</Button>
              </Box>
            </form>
          </>
        );
      }}
    </SidePanel>
  );
}

export function AccountDraftPanel({
  account,
  currencies,
  onClose,
  onDelete,
  onSave,
}: {
  account?: GroupAccountDraft;
  currencies: string[];
  onClose: () => void;
  onDelete: () => void;
  onSave: (account: GroupAccountDraft) => void;
}) {
  const initialType = account?.type || "bank";
  const [type, setType] = useState(initialType);
  const [icon, setIcon] = useState(account?.icon || accountTypeDefaults[initialType].icon);
  const [color, setColor] = useState(account?.color || accountTypeDefaults[initialType].color);
  const [name, setName] = useState(account?.name || "");
  const [currency, setCurrency] = useState(account?.currency || currencies[0]);
  const [initialBalance, setInitialBalance] = useState(account?.initial_balance || "0.00");
  const [error, setError] = useState("");
  const closeRef = useRef(onClose);
  const currencyOptions = Array.from(new Set([...currencies, currency])).filter(Boolean);

  const selectType = (nextType: string) => {
    setType(nextType);
    if (account) return;
    setIcon(accountTypeDefaults[nextType].icon);
    setColor(accountTypeDefaults[nextType].color);
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim()) return setError("Name is required.");
    if (!currency) return setError("Currency is required.");
    if (!initialBalance.trim() || !Number.isFinite(Number(initialBalance.replace(",", ".")))) return setError("Initial balance must be a number.");
    onSave({ name: name.trim(), type, icon, color, currency, initial_balance: Number(initialBalance.replace(",", ".")).toFixed(2) });
    closeRef.current();
  };

  return (
    <SidePanel ariaLabelledBy="setupAccountPanelTitle" className="account-modal group-setup-side-panel" onClose={onClose}>
      {({ close }) => {
        closeRef.current = close;
        return (
          <>
            <PanelHeader title={account ? "Edit account" : "Add account"} titleId="setupAccountPanelTitle" onClose={close} />
            <form className="records-form account-form" onSubmit={submit}>
              <Box className="account-controls-row">
                <IconColorPicker color={color} icon={icon} iconDataAttribute="account" pickerId="setupAccountIconPicker" pickerLabel="Account icon" onColorChange={setColor} onIconChange={setIcon} />
                <TextField fullWidth required label="Name" value={name} onChange={(event) => { setName(event.target.value); setError(""); }} />
              </Box>
              <Box className="account-type-toggle">
                <ButtonGroup variant="outlined" aria-label="Account type">
                  {Object.entries(accountTypeDefaults).map(([value, option]) => <Button key={value} type="button" variant={type === value ? "contained" : "outlined"} onClick={() => selectType(value)}>{option.label}</Button>)}
                </ButtonGroup>
              </Box>
              <TextField select required label="Currency" value={currency} onChange={(event) => setCurrency(String(event.target.value))}>
                {currencyOptions.map((code) => <MenuItem key={code} value={code}>{code}</MenuItem>)}
              </TextField>
              <TextField required label="Initial balance" inputMode="decimal" value={initialBalance} onChange={(event) => setInitialBalance(event.target.value)} />
              {error ? <p className="records-error">{error}</p> : null}
              <Box className="records-modal-actions">
                {account ? <Button variant="outlined" color="error" type="button" startIcon={<DeleteOutlineIcon />} onClick={() => { onDelete(); close(); }}>Delete account</Button> : null}
                <Button variant="contained" type="submit">{account ? "Save changes" : "Add account"}</Button>
              </Box>
            </form>
          </>
        );
      }}
    </SidePanel>
  );
}

export function SetupEntityIcon({ icon, color }: { icon: string; color: string }) {
  return <CategoryIcon icon={icon} color={color} />;
}
